import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  SmartToy as AgentIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import { agentsApi } from '../services/api';

function Agents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    endpoint_url: '',
    api_key: '',
    api_key_header: 'Authorization',
    payload_template: '{\n  "message": "{{question}}"\n}',
    response_path: '$.response',
    headers: '{}',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await agentsApi.list();
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.endpoint_url.trim()) return;
    
    setSaving(true);
    setError(null);
    
    try {
      let payloadTemplate, headers;
      
      try {
        payloadTemplate = JSON.parse(formData.payload_template);
      } catch {
        setError('Payload template inválido. Deve ser um JSON válido.');
        setSaving(false);
        return;
      }
      
      try {
        headers = JSON.parse(formData.headers);
      } catch {
        setError('Headers inválidos. Deve ser um JSON válido.');
        setSaving(false);
        return;
      }
      
      await agentsApi.create({
        name: formData.name,
        description: formData.description,
        endpoint_url: formData.endpoint_url,
        api_key: formData.api_key,
        api_key_header: formData.api_key_header,
        payload_template: payloadTemplate,
        response_path: formData.response_path,
        headers: headers,
      });
      
      setDialogOpen(false);
      setFormData({
        name: '',
        description: '',
        endpoint_url: '',
        api_key: '',
        api_key_header: 'Authorization',
        payload_template: '{\n  "message": "{{question}}"\n}',
        response_path: '$.response',
        headers: '{}',
      });
      loadAgents();
    } catch (error) {
      console.error('Error creating agent:', error);
      setError('Erro ao criar agente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir este agente?')) return;
    
    try {
      await agentsApi.delete(id);
      loadAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
    }
  };

  const handleTestConnection = async (id, e) => {
    e.stopPropagation();
    setTestingId(id);
    setTestResult(null);
    
    try {
      const response = await agentsApi.testConnection(id);
      setTestResult({ id, ...response.data });
    } catch (error) {
      setTestResult({ id, success: false, message: 'Erro ao testar conexão' });
    } finally {
      setTestingId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Agentes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Novo Agente
        </Button>
      </Box>

      {agents.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AgentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Nenhum agente cadastrado
              </Typography>
              <Typography color="textSecondary" mb={2}>
                Cadastre um agente para começar a realizar testes
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Cadastrar Primeiro Agente
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {agents.map((agent) => (
            <Grid item xs={12} sm={6} md={4} key={agent.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => navigate(`/agents/${agent.id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6" noWrap>
                      {agent.name}
                    </Typography>
                    <Chip
                      label={agent.is_active ? 'Ativo' : 'Inativo'}
                      size="small"
                      color={agent.is_active ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                    }}
                  >
                    {agent.description || 'Sem descrição'}
                  </Typography>
                  <Typography variant="body2" noWrap color="textSecondary">
                    {agent.endpoint_url}
                  </Typography>
                  
                  {testResult && testResult.id === agent.id && (
                    <Alert
                      severity={testResult.success ? 'success' : 'error'}
                      sx={{ mt: 2 }}
                      icon={testResult.success ? <SuccessIcon /> : <ErrorIcon />}
                    >
                      {testResult.message}
                      {testResult.response_time_ms > 0 && (
                        <Typography variant="caption" display="block">
                          Tempo: {testResult.response_time_ms}ms
                        </Typography>
                      )}
                    </Alert>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={testingId === agent.id ? <CircularProgress size={16} /> : <TestIcon />}
                    onClick={(e) => handleTestConnection(agent.id, e)}
                    disabled={testingId === agent.id}
                  >
                    Testar
                  </Button>
                  <Button size="small" onClick={() => navigate(`/agents/${agent.id}`)}>
                    Detalhes
                  </Button>
                  <Box flexGrow={1} />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDelete(agent.id, e)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Novo Agente</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="URL do Endpoint"
                fullWidth
                value={formData.endpoint_url}
                onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })}
                placeholder="https://api.example.com/chat"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="API Key"
                fullWidth
                type="password"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                helperText="Valor da chave de autenticação"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome do Header da API Key"
                fullWidth
                value={formData.api_key_header}
                onChange={(e) => setFormData({ ...formData, api_key_header: e.target.value })}
                helperText="Ex: Authorization, x-api-key, Api-Key"
                placeholder="Authorization"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Response Path"
                fullWidth
                value={formData.response_path}
                onChange={(e) => setFormData({ ...formData, response_path: e.target.value })}
                helperText="JSONPath para extrair resposta (ex: $.response)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Payload Template (JSON)"
                fullWidth
                multiline
                rows={4}
                value={formData.payload_template}
                onChange={(e) => setFormData({ ...formData, payload_template: e.target.value })}
                helperText="Use {{question}} como placeholder para a pergunta"
                sx={{ fontFamily: 'monospace' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Headers Adicionais (JSON)"
                fullWidth
                multiline
                rows={2}
                value={formData.headers}
                onChange={(e) => setFormData({ ...formData, headers: e.target.value })}
                helperText="Headers extras para a requisição"
                sx={{ fontFamily: 'monospace' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!formData.name.trim() || !formData.endpoint_url.trim() || saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Agents;
